/**
 * Ingredient Helper Utilities
 *
 * Handles both legacy nested format and new simple format for ingredients.
 * This allows gradual migration without breaking existing recipes.
 */

// Legacy format (over-engineered)
interface LegacyIngredient {
  amount: { id?: string; name: string };
  ingredientName: { id?: string; name: string };
  unit?: { id?: string; name: string } | null;
  notes?: { id?: string; note: string } | null;
}

// New simple format
export interface SimpleIngredient {
  amount: string;
  name: string;
  unit?: string;
  notes?: string;
}

// Union type for both formats
export type AnyIngredient = LegacyIngredient | SimpleIngredient | Record<string, unknown>;

/**
 * Check if ingredient is in legacy nested format
 */
function isLegacyFormat(ingredient: AnyIngredient): ingredient is LegacyIngredient {
  return (
    ingredient &&
    typeof ingredient === 'object' &&
    'ingredientName' in ingredient &&
    typeof (ingredient as LegacyIngredient).ingredientName === 'object'
  );
}

/**
 * Check if ingredient is in simple format
 */
function isSimpleFormat(ingredient: AnyIngredient): ingredient is SimpleIngredient {
  return (
    ingredient &&
    typeof ingredient === 'object' &&
    'name' in ingredient &&
    typeof (ingredient as SimpleIngredient).name === 'string'
  );
}

/**
 * Normalize any ingredient format to simple display values.
 * Works with both legacy nested format and new simple format.
 *
 * @example
 * // Legacy format input:
 * { amount: { name: "2" }, ingredientName: { name: "cups flour" }, unit: { name: "cups" }, notes: { note: "sifted" } }
 *
 * // Simple format input:
 * { amount: "2", name: "flour", unit: "cups", notes: "sifted" }
 *
 * // Both return:
 * { amount: "2", name: "flour", unit: "cups", notes: "sifted" }
 */
export function normalizeIngredient(ingredient: AnyIngredient): SimpleIngredient {
  if (!ingredient || typeof ingredient !== 'object') {
    return { amount: '', name: '', unit: '', notes: '' };
  }

  // Handle legacy nested format
  if (isLegacyFormat(ingredient)) {
    return {
      amount: ingredient.amount?.name || '',
      name: ingredient.ingredientName?.name || '',
      unit: ingredient.unit?.name || undefined,
      notes: ingredient.notes?.note || undefined,
    };
  }

  // Handle simple format (or close to it)
  if (isSimpleFormat(ingredient)) {
    return {
      amount: ingredient.amount || '',
      name: ingredient.name || '',
      unit: ingredient.unit || undefined,
      notes: ingredient.notes || undefined,
    };
  }

  // Fallback: try to extract what we can
  const ing = ingredient as Record<string, unknown>;
  return {
    amount: String(
      (ing.amount as { name?: string })?.name ||
      ing.amount ||
      ''
    ),
    name: String(
      (ing.ingredientName as { name?: string })?.name ||
      (ing.name as { name?: string })?.name ||
      ing.ingredientName ||
      ing.name ||
      ''
    ),
    unit: (ing.unit as { name?: string })?.name ||
      (typeof ing.unit === 'string' ? ing.unit : undefined),
    notes: (ing.notes as { note?: string })?.note ||
      (typeof ing.notes === 'string' && ing.notes ? ing.notes : undefined),
  };
}

/**
 * Format ingredient for display as a single string.
 * Handles the "to taste" edge case where amount comes after name.
 *
 * @example
 * formatIngredientDisplay({ amount: "2", unit: "cups", name: "flour" })
 * // Returns: "2 cups flour"
 *
 * formatIngredientDisplay({ amount: "to taste", name: "salt and pepper" })
 * // Returns: "salt and pepper to taste"
 */
export function formatIngredientDisplay(ingredient: AnyIngredient): string {
  const normalized = normalizeIngredient(ingredient);
  const { amount, unit, name } = normalized;

  // Handle "to taste" - amount goes after ingredient name
  const toTastePattern = /^to\s+taste$/i;
  if (toTastePattern.test(amount)) {
    return `${name} to taste`.trim();
  }

  // Standard format: amount unit name
  const parts = [amount, unit, name].filter(Boolean);
  return parts.join(' ').trim();
}

/**
 * Convert legacy ingredient to simple format for migration.
 * Use this when writing recipes back to Firestore.
 */
export function convertToSimpleFormat(ingredient: AnyIngredient): SimpleIngredient {
  const normalized = normalizeIngredient(ingredient);

  // Clean up - remove undefined/empty optional fields
  const result: SimpleIngredient = {
    amount: normalized.amount,
    name: normalized.name,
  };

  if (normalized.unit) {
    result.unit = normalized.unit;
  }

  if (normalized.notes) {
    result.notes = normalized.notes;
  }

  return result;
}
